package com.eladfinish.bible.contest.screens.search.usecases.copy

import com.eladfinish.bible.contest.common.usecases.RemoveNikudAndTeamimIfNeedUseCase
import com.eladfinish.bible.contest.domain.catalog.BibleCatalog
import com.eladfinish.bible.contest.features.copyOptions.CopyOptions
import com.eladfinish.bible.contest.screens.search.model.VerseUiModel
import com.eladfinish.bible.contest.screens.search.ui.HighlightType
import com.eladfinish.bible.contest.screens.search.ui.toHighlightSigns
import com.eladfinish.bible.contest.screens.search.ui.wrapWithSigns
import com.eladfinish.bible.contest.utils.addGershaim
import com.eladfinish.bible.contest.utils.toOtiot

class PrepareSingleSearchResultToCopyUseCase(
    private val removeNikudAndTeamimIfNeedUseCase: RemoveNikudAndTeamimIfNeedUseCase,
) {

    operator fun invoke(
        item: VerseUiModel,
        copyOptions: CopyOptions,
        highlightType: HighlightType,
    ): String {
        val verseText = buildVerseText(
            item = item,
            highlightType = highlightType,
            copyOptions = copyOptions,
        )

        val normalized = removeNikudAndTeamimIfNeedUseCase(
            text = verseText,
            copyOptions = copyOptions,
        )

        return if (copyOptions.removeVerseOrigin) {
            normalized
        } else {
            "$normalized ${item.buildSourceForCopy()}"
        }
    }

    private fun buildVerseText(
        item: VerseUiModel,
        highlightType: HighlightType,
        copyOptions: CopyOptions,
    ): String {
        if (copyOptions.copyOnlyHighlights) {
            return item.extractHighlights(
                highlightType = highlightType,
            )
        }

        if (highlightType == HighlightType.NONE) {
            return item.text
        }

        return item.text.withMarkers(
            ranges = item.highlightedRanges,
            highlightType = highlightType,
        )
    }

    private fun VerseUiModel.extractHighlights(
        highlightType: HighlightType,
    ): String =
        highlightedRanges
            .mapNotNull { range ->
                text.substringOrNull(range)
            }
            .joinToString(separator = " ")
            .wrapWithSigns(highlightType)

    private fun String.withMarkers(
        ranges: List<IntRange>,
        highlightType: HighlightType,
    ): String {
        val (startSign, endSign) = highlightType.toHighlightSigns()
        val builder = StringBuilder()
        var currentIndex = 0

        ranges
            .sortedBy { it.first }
            .forEach { range ->
                val start = range.first.coerceIn(0, length)
                val endExclusive = range.last.plus(1).coerceIn(0, length)

                if (start !in currentIndex..<endExclusive) {
                    return@forEach
                }

                builder.append(substring(currentIndex, start))
                builder.append(startSign)
                builder.append(substring(start, endExclusive))
                builder.append(endSign)

                currentIndex = endExclusive
            }

        builder.append(substring(currentIndex, length))

        return builder.toString()
    }

    private fun String.substringOrNull(
        range: IntRange,
    ): String? {
        val start = range.first.coerceIn(0, length)
        val endExclusive = range.last.plus(1).coerceIn(0, length)

        return if (start < endExclusive) {
            substring(start, endExclusive)
        } else {
            null
        }
    }

    private fun VerseUiModel.buildSourceForCopy(): String {
        val bookName = BibleCatalog.books
            .getOrNull(bookIndex)
            ?.hebrew
            ?: source

        val chapter = chapterIndex
            .toOtiot()
            .addGershaim()

        val verseText = verse.addGershaim()

        return "($bookName $chapter, $verseText)"
    }
}
